import { useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { open, close } from '@/store/uiSlice';

/**
 * Modal keys that are tracked in the URL as ?modal=<key>.
 * 'history' and 'admin' are AppBar toggles that don't need URL tracking.
 */
const TRACKED_MODALS = [
    'menu',
    'user',
    'mailbox',
    'filter',
    'event',
    'absent',
    'agenda',
    'files',
    'schedule',
    'categoryRequest',
    'categoryRequestsReview',
    'scheduleTemplateSets',
    'changeRequests',
    'yearSummary',
    'personalForecast',
    'handbook',
] as const;

type TrackedModal = (typeof TRACKED_MODALS)[number];

function isTracked(key: string): key is TrackedModal {
    return TRACKED_MODALS.includes(key as TrackedModal);
}

/**
 * Bidirectional sync between Redux ui.open state and ?modal=<key> URL param.
 *
 * Redux → URL: when a modal opens in Redux, push ?modal=<key> so back closes it.
 * URL → Redux: when the URL changes (e.g. browser back), update Redux accordingly.
 */
export function useModalNavigation() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const uiOpen = useAppSelector((state) => state.ui.open);

    const urlModal = searchParams.get('modal') as TrackedModal | null;
    const updatingFromUrl = useRef(false);
    const updatingFromRedux = useRef(false);

    // Updated synchronously during render — always current when effects run
    const locationRef = useRef(location);
    locationRef.current = location;

    // ── URL → Redux ──────────────────────────────────────────────────────────
    // Browser back/forward changed the URL — sync to Redux
    useEffect(() => {
        if (updatingFromRedux.current) return;
        updatingFromUrl.current = true;

        if (urlModal && isTracked(urlModal)) {
            if (!uiOpen[urlModal]) {
                dispatch(open(urlModal));
            }
        } else {
            // No modal in URL — close any tracked modal that's open
            TRACKED_MODALS.forEach((key) => {
                if (uiOpen[key]) dispatch(close(key));
            });
        }

        updatingFromUrl.current = false;
    }, [urlModal]);

    useEffect(() => {
        if (updatingFromUrl.current) return;

        const openModal = TRACKED_MODALS.find((key) => uiOpen[key]);

        if (openModal) {
            const currentUrlModal = new URLSearchParams(locationRef.current.search).get('modal');
            if (currentUrlModal !== openModal) {
                navigate(`?modal=${openModal}`, { replace: currentUrlModal !== null });
            }
        }
    }, [TRACKED_MODALS.map((k) => uiOpen[k]).join(',')]);
}
